import { FSRSCard, knolToFSRSCard } from "@core/fsrs/scheduler";
import { excludeUndefined, filterIterable, mapIterable } from "@lib/iterable";
import MapView from "@lib/mapView";
import { IDeck } from "@models/deck";
import { isDeckFSRS } from "@models/deckSettings";
import { IKnol } from "@models/knol";
import { LensSpec, useLens } from "hooks/data/useLens";
import { ID } from "types/ID";
import { superCache } from "../../hooks/data/superCache";

export function numDueKnols(
  decks: MapView<ID, IDeck>,
  knols: MapView<ID, IKnol>,
  now: number,
): number | undefined {
  // Find which decks are FSRS.
  const fsrsDeckIDs = new Set<ID>();
  for (const deck of decks.values()) {
    if (isDeckFSRS(deck)) {
      fsrsDeckIDs.add(deck.id);
    }
  }

  if (fsrsDeckIDs.size < 1) {
    return undefined;
  }

  // Count due knols in FSRS decks.
  const knolsInFSRSDecks = filterIterable(knols, ([, knol]) => fsrsDeckIDs.has(knol.deck_id));
  const maybeFSRSKnols = mapIterable(knolsInFSRSDecks, ([, knol]) => knolToFSRSCard(knol, now));
  const fsrsKnols = excludeUndefined(maybeFSRSKnols);
  const counts = calculateDetailedDueCounts(fsrsKnols, now);

  return counts.now;
}

// undefined indicates no FSRS decks matching lens.
export function useDueCount(lensSpec: LensSpec): number | undefined {
  const lens = useLens(lensSpec, { listenWhenOffscreen: true });
  const now = Date.now();
  return numDueKnols(lens.decks, lens.filteredKnols, now);
}

/**
 * Detailed due date counts for FSRS cards
 */
export interface DetailedDueCounts {
  new: number;
  now: number;
  within24hr: number;
  tomorrow: number;
  within1wk: number;
  future: number;
}

/**
 * Simple due date counts for FSRS cards (used in deck list)
 */
export interface SimpleDueCounts {
  today: number;
  tomorrow: number;
  next7d: number;
}

/**
 * Calculate detailed due date counts for FSRS cards
 * This is the same logic used by the FSRS histogram
 */
export function calculateDetailedDueCounts(
  cards: Iterable<FSRSCard>,
  now: number = Date.now(),
): DetailedDueCounts {
  const oneDayMs = 1000 * 60 * 60 * 24;
  const nowCutoff = now;
  const within24hrCutoff = now + oneDayMs;
  const tomorrowStart = new Date(now);
  tomorrowStart.setHours(0, 0, 0, 0);
  tomorrowStart.setDate(tomorrowStart.getDate() + 1);
  const tomorrowEnd = new Date(tomorrowStart);
  tomorrowEnd.setDate(tomorrowEnd.getDate() + 1);
  const week1Cutoff = now + oneDayMs * 7;

  const counts: DetailedDueCounts = {
    new: 0,
    now: 0,
    within24hr: 0,
    tomorrow: 0,
    within1wk: 0,
    future: 0,
  };

  for (const card of cards) {
    // Count new cards separately
    if (card.state === "New") {
      counts.new++;
      continue;
    }

    // For non-new cards, count by due date
    if (card.due <= nowCutoff) {
      counts.now++;
    } else if (card.due <= within24hrCutoff) {
      // Check if it's specifically tomorrow
      if (card.due >= tomorrowStart.getTime() && card.due < tomorrowEnd.getTime()) {
        counts.tomorrow++;
      } else {
        counts.within24hr++;
      }
    } else if (card.due <= week1Cutoff) {
      counts.within1wk++;
    } else {
      counts.future++;
    }
  }

  return counts;
}

/**
 * Calculate simple due date counts for FSRS cards
 * This is the same logic used by the deck list and FSRS scheduler
 */
function calculateSimpleDueCounts(cards: FSRSCard[], now: number = Date.now()): SimpleDueCounts {
  const todayCutoff = now;
  const tomorrowCutoff = now + 1000 * 60 * 60 * 24;
  const weekCutoff = now + 1000 * 60 * 60 * 24 * 7;

  let today = 0;
  let tomorrow = 0;
  let next7d = 0;

  for (const card of cards) {
    // Exclude new cards from due counts
    if (card.state === "New") {
      continue;
    }
    if (card.due <= todayCutoff) {
      today++;
    } else if (card.due <= tomorrowCutoff) {
      tomorrow++;
    } else if (card.due <= weekCutoff) {
      next7d++;
    }
  }

  return { today, tomorrow, next7d };
}

/**
 * Get FSRS cards for a deck using the most up-to-date data from supercache
 */
function getFSRSCardsForDeck(deckID: ID, now: number = Date.now()): FSRSCard[] {
  if (!superCache.knols || !superCache.deckIDToKnolIDs) {
    return [];
  }

  const knolIDs = superCache.deckIDToKnolIDs.get(deckID);
  if (!knolIDs) {
    return [];
  }

  const cards: FSRSCard[] = [];
  for (const knolID of knolIDs) {
    const knol = superCache.knols.get(knolID);
    if (knol) {
      const card = knolToFSRSCard(knol, now);
      if (card) {
        cards.push(card);
      }
    }
  }

  return cards;
}

/**
 * Get FSRS cards from an iterable of knols
 */
export function getFSRSCardsFromKnols(
  knols: Iterable<IKnol>,
  now: number = Date.now(),
): FSRSCard[] {
  const cards: FSRSCard[] = [];
  for (const knol of knols) {
    const card = knolToFSRSCard(knol, now);
    if (card) {
      cards.push(card);
    }
  }
  return cards;
}

/**
 * Check if a deck is using FSRS
 */

/**
 * Get detailed due counts for a deck using supercache data
 * Returns null if deck is not FSRS or data is not available
 */
export function getDetailedDueCountsForDeck(
  deckID: ID,
  now: number = Date.now(),
): DetailedDueCounts | null {
  const deck = superCache.decks?.get(deckID);
  if (!isDeckFSRS(deck)) {
    return null;
  }

  const cards = getFSRSCardsForDeck(deckID, now);
  return calculateDetailedDueCounts(cards, now);
}

/**
 * Get simple due counts for a deck using supercache data
 * Returns null if deck is not FSRS or data is not available
 */
export function getSimpleDueCountsForDeck(
  deckID: ID,
  now: number = Date.now(),
): SimpleDueCounts | null {
  const deck = superCache.decks?.get(deckID);
  if (!isDeckFSRS(deck)) {
    return null;
  }

  const cards = getFSRSCardsForDeck(deckID, now);
  return calculateSimpleDueCounts(cards, now);
}

/**
 * Get detailed due counts from an iterable of knols
 * Returns null if deck is not FSRS
 */
export function getDetailedDueCountsFromKnols(
  knols: Iterable<IKnol>,
  deck: IDeck | undefined,
  now: number = Date.now(),
): DetailedDueCounts | null {
  if (!isDeckFSRS(deck)) {
    return null;
  }

  const cards = getFSRSCardsFromKnols(knols, now);
  return calculateDetailedDueCounts(cards, now);
}

/**
 * Get simple due counts from an iterable of knols
 * Returns null if deck is not FSRS
 */
export function getSimpleDueCountsFromKnols(
  knols: Iterable<IKnol>,
  deck: IDeck | undefined,
  now: number = Date.now(),
): SimpleDueCounts | null {
  if (!isDeckFSRS(deck)) {
    return null;
  }

  const cards = getFSRSCardsFromKnols(knols, now);
  return calculateSimpleDueCounts(cards, now);
}
